import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { UserForm } from "../../components/users";
import { useUser, useUpdateUser } from "../../features/users/hooks";
import { UpdateUserInput } from "@shared/schemas";

/**
 * Page for editing an existing user
 */
const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const {
    user,
    isLoading: isLoadingUser,
    error: userError,
    refetch,
  } = useUser(id);
  const { updateUser, isLoading: isUpdating } = useUpdateUser();
  const [error, setError] = useState<string | undefined>(undefined);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // Handle form submission
  const handleSubmit = async (data: Omit<UpdateUserInput, "id">) => {
    if (!id) return;

    const updateData: UpdateUserInput = {
      id,
      ...data,
    };

    const result = await updateUser(updateData);

    if (result.success) {
      setNotification({
        open: true,
        message: "User updated successfully",
        severity: "success",
      });
    } else {
      setError(result.error);
    }
  };

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  if (isLoadingUser) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-800 dark:text-red-200">
            {userError?.message || "Failed to load user details"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumbs */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li>
            <div className="flex items-center">
              <button
                onClick={() => navigate("/")}
                className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary"
              >
                Dashboard
              </button>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <button
                onClick={() => navigate("/users")}
                className="ml-1 text-gray-700 hover:text-primary md:ml-2 dark:text-gray-300 dark:hover:text-primary"
              >
                Users
              </button>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              <span className="ml-1 text-gray-500 md:ml-2 dark:text-gray-400">
                Edit User
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <h1 className="text-3xl font-bold text-gray-500 dark:text-white mb-2">
        Edit User: {user.firstName} {user.lastName}
      </h1>

      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Update user details and permissions
      </p>

      <div className="bg-white dark:bg-darkmode-800 rounded-lg p-6">
        <UserForm
          user={user}
          isEditMode={true}
          onSubmit={handleSubmit}
          isLoading={isUpdating}
          error={error}
          onUserUpdate={() => refetch()}
          onCancel={() => navigate("/users")}
        />
      </div>

      {/* Notification */}
      {notification.open && (
        <div className="fixed bottom-4 right-4 z-50">
          <div
            className={`
            max-w-xl w-full shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden
            ${notification.severity === "success" ? "bg-green-50 dark:bg-green-900/20" : ""}
            ${notification.severity === "error" ? "bg-red-50 dark:bg-red-900/20" : ""}
            ${notification.severity === "warning" ? "bg-yellow-50 dark:bg-yellow-900/20" : ""}
            ${notification.severity === "info" ? "bg-blue-50 dark:bg-blue-900/20" : ""}
          `}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {notification.severity === "success" && (
                    <svg
                      className="h-6 w-6 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                  {notification.severity === "error" && (
                    <svg
                      className="h-6 w-6 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1 pt-0.5">
                  <p
                    className={`
                    text-sm font-medium
                    ${notification.severity === "success" ? "text-green-800 dark:text-green-200" : ""}
                    ${notification.severity === "error" ? "text-red-800 dark:text-red-200" : ""}
                    ${notification.severity === "warning" ? "text-yellow-800 dark:text-yellow-200" : ""}
                    ${notification.severity === "info" ? "text-blue-800 dark:text-blue-200" : ""}
                  `}
                  >
                    {notification.message}
                  </p>
                </div>
                <div className="ml-4 flex-shrink-0 flex">
                  <button
                    onClick={handleNotificationClose}
                    className={`
                      inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2
                      ${notification.severity === "success" ? "text-green-500 hover:text-green-600 focus:ring-green-500" : ""}
                      ${notification.severity === "error" ? "text-red-500 hover:text-red-600 focus:ring-red-500" : ""}
                      ${notification.severity === "warning" ? "text-yellow-500 hover:text-yellow-600 focus:ring-yellow-500" : ""}
                      ${notification.severity === "info" ? "text-blue-500 hover:text-blue-600 focus:ring-blue-500" : ""}
                    `}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditUserPage;
